import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Zap, X } from "lucide-react";
import { useAuth, API } from "../App";

/**
 * Live sale ticker — pinned bottom strip on the admin dashboard.
 * Polls /api/admin/live-sales every 15s. Newly-arrived rows animate in as a
 * toast-style pill. Test-account sales are visually muted so they don't get
 * confused with real revenue.
 *
 * Auto-hides if the admin dismisses; re-shows on next page load.
 */
const LiveSaleTicker = () => {
  const { token, user } = useAuth();
  const [sales, setSales] = useState([]);
  const [visible, setVisible] = useState(true);
  const seenIdsRef = useRef(new Set());

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    if (!isAdmin || !token) return undefined;
    let cancelled = false;
    let timer = null;

    const relTime = (iso) => {
      const then = new Date(iso).getTime();
      const diff = Math.max(0, (Date.now() - then) / 1000);
      if (diff < 60) return `${Math.floor(diff)}s ago`;
      if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
      return `${Math.floor(diff / 3600)}h ago`;
    };

    const fetchSales = async () => {
      try {
        const res = await axios.get(`${API}/admin/live-sales?since_seconds=900&limit=15`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const rows = res.data?.sales || [];
        // Tag freshness for the animation trigger
        const withMeta = rows.map((r) => ({
          ...r,
          _rel: relTime(r.created_at),
          _isNew: !seenIdsRef.current.has(r.id),
        }));
        rows.forEach((r) => seenIdsRef.current.add(r.id));
        setSales(withMeta);
      } catch {
        // silent — ticker is a nice-to-have
      }
    };

    fetchSales();
    timer = setInterval(fetchSales, 15000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [isAdmin, token]);

  if (!isAdmin || !visible || sales.length === 0) return null;

  const formatMoney = (amt, cur) => {
    const s = Number(amt || 0).toFixed(2);
    return `${(cur || "usd").toUpperCase() === "CAD" ? "CA$" : "$"}${s}`;
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-[#050810] via-[#0A0E18] to-[#050810] border-t border-[#EC008C]/30"
      data-testid="live-sale-ticker"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center gap-4">
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EC008C] opacity-70"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#EC008C]"></span>
          </span>
          <Zap className="w-4 h-4 text-[#EC008C]" />
          <span className="text-xs font-black uppercase tracking-widest text-[#EC008C]">Live</span>
        </div>
        <div
          className="flex-1 min-w-0 overflow-x-auto whitespace-nowrap scrollbar-none"
          style={{ scrollbarWidth: "none" }}
        >
          <div className="inline-flex gap-3 items-center">
            {sales.map((s) => (
              <div
                key={s.id}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
                  s.is_test_account
                    ? "bg-[#FFD466]/10 border-[#FFD466]/30 text-[#FFD466]"
                    : "bg-[#8DB87A]/10 border-[#8DB87A]/30 text-white"
                }`}
                data-testid={`live-sale-row-${s.id}`}
              >
                {s.is_test_account && (
                  <span className="text-[9px] font-black uppercase tracking-widest bg-[#FFD466]/25 px-1.5 py-0.5 rounded">
                    TEST
                  </span>
                )}
                <span className="font-semibold truncate max-w-[220px]">{s.product_name || "Sale"}</span>
                <span className="text-[#8DB87A] font-mono">{formatMoney(s.amount, s.currency)}</span>
                {s.artist_name && (
                  <span className="text-[#8A92A0]">· {s.artist_name}</span>
                )}
                <span className="text-[#6A7280]">· {s._rel}</span>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={() => setVisible(false)}
          className="text-[#8A92A0] hover:text-white flex-shrink-0"
          data-testid="live-sale-ticker-close"
          aria-label="Hide ticker"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default LiveSaleTicker;
